import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'

interface DeskLinkProps {
    href: string
    children: ReactNode
    /** Corner of the desk it is taped to. */
    place?: 'top-left' | 'top-right'
    /** Degrees. Nothing on this desk is square to the edge. */
    rot?: number
}

/* A label taped to the mat: cream card, hard offset shadow, mono uppercase and
 * tracked — the same chrome the section's own back / CTA controls wear, so a
 * link between pages does not read as a different site's furniture. */
export const DeskLink: FC<DeskLinkProps> = ({
    href,
    children,
    place = 'top-left',
    rot = -1.4
}): ReactNode => {
    return (
        <>
            <Link
                href={href}
                className={`desk-link desk-link--${place}`}
                style={{ '--rot': `${rot}deg` } as CSSProperties}
            >
                {children}
            </Link>

            <style>{`
                .desk-link {
                    position: absolute;
                    z-index: 70;
                    padding: 10px 15px 9px;
                    border-radius: 2px;
                    background: #F3EDE0;
                    color: #191510;
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                    font-size: 11px;
                    font-weight: 500;
                    letter-spacing: .12em;
                    text-transform: uppercase;
                    text-decoration: none;
                    white-space: nowrap;
                    box-shadow: 2px 5px 0 rgba(20,16,10,.28);
                    transform: rotate(var(--rot, -1.4deg));
                    transform-origin: 50% 50%;
                    transition: box-shadow .16s ease, background .16s ease, transform .16s ease;
                    animation: deskLinkIn .7s .35s cubic-bezier(.16,1,.3,1) both;
                }
                .desk-link--top-left { left: 3.4vw; top: 3.6vh; }
                .desk-link--top-right { right: 3.4vw; top: 3.6vh; }
                .desk-link:hover {
                    background: #FFFCF4;
                    box-shadow: 3px 8px 0 rgba(20,16,10,.34);
                    transform: rotate(var(--rot, -1.4deg)) translateY(-1px);
                }
                .desk-link:active { box-shadow: 1px 2px 0 rgba(20,16,10,.34); }
                .desk-link:focus-visible {
                    outline: 2px dashed #F2E9CF;
                    outline-offset: 5px;
                }
                @keyframes deskLinkIn {
                    from { opacity: 0; transform: rotate(var(--rot, -1.4deg)) translateY(-12px); }
                }
                @media (max-width: 760px) {
                    .desk-link { font-size: 10px; padding: 8px 11px 7px; }
                    .desk-link--top-left { left: 5vw; top: 2.2vh; }
                    .desk-link--top-right { right: 5vw; top: 2.2vh; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .desk-link { animation-duration: .01s; }
                }
            `}</style>
        </>
    )
}
